import React, { useMemo } from 'react';
import {
  useReactTable,
  getCoreRowModel,
  getSortedRowModel,
  flexRender,
} from '@tanstack/react-table';
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Chip,
  Button,
  Box,
  Typography,
  useTheme,
  useMediaQuery,
} from '@mui/material';
import RepeatIcon from '@mui/icons-material/Repeat';

function CustomerBookingHistoryTable({ bookings, onRebook }) {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));

  const getStatusColor = (status) => {
    const colors = {
      confirmed: 'success',
      pending: 'warning',
      cancelled: 'error',
      completed: 'info',
    };
    return colors[status] || 'default';
  };

  const getPaymentStatusColor = (status) => {
    const colors = {
      completed: 'success',
      pending: 'warning',
      failed: 'error',
    };
    return colors[status] || 'warning';
  };

  const columns = useMemo(
    () => [
      {
        accessorKey: 'booking_date',
        header: 'Date',
        cell: ({ getValue }) => (
          <Typography variant="body2" fontWeight={500}>
            {getValue()}
          </Typography>
        ),
      },
      {
        accessorKey: 'start_time',
        header: 'Time',
        cell: ({ getValue }) => (
          <Typography variant="body2">{getValue()}</Typography>
        ),
      },
      {
        accessorKey: 'service_name',
        header: 'Service',
        cell: ({ getValue }) => (
          <Typography variant="body2">{getValue()}</Typography>
        ),
      },
      {
        accessorKey: 'service_price',
        header: 'Price',
        cell: ({ getValue, row }) => (
          <Box>
            {row.original.discount_amount > 0 ? (
              <>
                <Typography variant="body2" fontWeight={500} color="success.main">
                  £{(getValue() - row.original.discount_amount).toFixed(2)}
                </Typography>
                <Typography
                  variant="caption"
                  color="text.secondary"
                  sx={{ textDecoration: 'line-through' }}
                >
                  £{getValue()}
                </Typography>
                <Typography variant="caption" display="block" color="secondary.main">
                  {row.original.discount_code}
                </Typography>
              </>
            ) : (
              <Typography variant="body2" fontWeight={500}>
                £{getValue()}
              </Typography>
            )}
          </Box>
        ),
      },
      {
        accessorKey: 'payment_status',
        header: 'Payment',
        cell: ({ getValue }) => (
          <Chip
            label={getValue() || 'pending'}
            color={getPaymentStatusColor(getValue())}
            size="small"
            sx={{ fontWeight: 500 }}
          />
        ),
      },
      {
        accessorKey: 'status',
        header: 'Status',
        cell: ({ getValue }) => (
          <Chip
            label={getValue()}
            color={getStatusColor(getValue())}
            size="small"
            sx={{ fontWeight: 500 }}
          />
        ),
      },
      {
        id: 'actions',
        header: 'Actions',
        cell: ({ row }) => (
          <Button
            variant="outlined"
            size="small"
            color="primary"
            startIcon={<RepeatIcon />}
            onClick={() => onRebook(row.original)}
            sx={{ minWidth: 90 }}
          >
            Rebook
          </Button>
        ),
      },
    ],
    [onRebook]
  );

  const table = useReactTable({
    data: bookings,
    columns,
    getCoreRowModel: getCoreRowModel(),
    getSortedRowModel: getSortedRowModel(),
  });

  if (bookings.length === 0) {
    return (
      <Box
        sx={{
          p: 4,
          textAlign: 'center',
          bgcolor: 'info.light',
          borderRadius: 2,
          color: 'info.dark',
        }}
      >
        <Typography variant="body1">No bookings found for this customer.</Typography>
      </Box>
    );
  }

  // Mobile card view
  if (isMobile) {
    return (
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
        {bookings.map((booking) => (
          <Paper
            key={booking.id}
            elevation={2}
            sx={{
              p: 2,
              borderRadius: 2,
              border: '1px solid',
              borderColor: 'divider',
              background: 'linear-gradient(145deg, #ffffff 0%, #fafafa 100%)',
              transition: 'all 0.2s cubic-bezier(0.4, 0, 0.2, 1)',
              '&:hover': {
                transform: 'translateY(-2px)',
                boxShadow: '0 8px 16px -4px rgba(0, 0, 0, 0.12), 0 4px 8px -2px rgba(0, 0, 0, 0.08)',
              },
            }}
          >
            <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1.5 }}>
              <Box>
                <Typography variant="body2" fontWeight={600}>
                  {booking.booking_date}
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  {booking.start_time}
                </Typography>
              </Box>
              <Chip
                label={booking.status}
                color={getStatusColor(booking.status)}
                size="small"
                sx={{ fontWeight: 500 }}
              />
            </Box>

            <Box sx={{ mb: 1.5 }}>
              <Typography variant="caption" color="text.secondary" display="block">
                Service
              </Typography>
              <Typography variant="body2" fontWeight={500}>
                {booking.service_name}
              </Typography>
            </Box>

            <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1.5 }}>
              <Box>
                <Typography variant="caption" color="text.secondary" display="block">
                  Price
                </Typography>
                {booking.discount_amount > 0 ? (
                  <>
                    <Typography variant="body2" fontWeight={600} color="success.main">
                      £{(booking.service_price - booking.discount_amount).toFixed(2)}
                    </Typography>
                    <Typography
                      variant="caption"
                      color="text.secondary"
                      sx={{ textDecoration: 'line-through' }}
                    >
                      £{booking.service_price}
                    </Typography>
                    <Typography variant="caption" display="block" color="secondary.main">
                      {booking.discount_code}
                    </Typography>
                  </>
                ) : (
                  <Typography variant="body2" fontWeight={600}>
                    £{booking.service_price}
                  </Typography>
                )}
              </Box>
              <Box sx={{ textAlign: 'right' }}>
                <Typography variant="caption" color="text.secondary" display="block">
                  Payment
                </Typography>
                <Chip
                  label={booking.payment_status || 'pending'}
                  color={getPaymentStatusColor(booking.payment_status)}
                  size="small"
                />
              </Box>
            </Box>

            <Box sx={{ pt: 1.5, borderTop: '1px solid', borderColor: 'divider' }}>
              <Button
                variant="contained"
                fullWidth
                size="small"
                startIcon={<RepeatIcon />}
                onClick={() => onRebook(booking)}
              >
                Rebook This Service
              </Button>
            </Box>
          </Paper>
        ))}
      </Box>
    );
  }

  // Desktop table view
  return (
    <TableContainer component={Paper} elevation={0} sx={{ border: '1px solid', borderColor: 'divider' }}>
      <Table>
        <TableHead>
          {table.getHeaderGroups().map((headerGroup) => (
            <TableRow key={headerGroup.id}>
              {headerGroup.headers.map((header) => (
                <TableCell key={header.id} sx={{ fontWeight: 600, bgcolor: 'grey.50' }}>
                  {flexRender(header.column.columnDef.header, header.getContext())}
                </TableCell>
              ))}
            </TableRow>
          ))}
        </TableHead>
        <TableBody>
          {table.getRowModel().rows.map((row) => (
            <TableRow key={row.id} hover>
              {row.getVisibleCells().map((cell) => (
                <TableCell key={cell.id}>
                  {flexRender(cell.column.columnDef.cell, cell.getContext())}
                </TableCell>
              ))}
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </TableContainer>
  );
}

export default CustomerBookingHistoryTable;
